// intake.repo — satu-satunya akses Supabase untuk modul Terima Servis.
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import type { Customer, Service } from '@core/types';

export async function listCustomers(): Promise<Customer[]> {
  return fetchAllPaged<Customer>((dari, sampai) => supabase()
    .from('customers').select('id,nama,kat,telp,wa,outlet').order('nama').range(dari, sampai));
}

export async function listServices(): Promise<Service[]> {
  const { data, error } = await supabase()
    .from('services').select('id,nama,modal,toko,user,garansi').order('nama');
  if (error) throw new Error(error.message);
  return (data ?? []) as Service[];
}

export async function listTechnicians(outlet: string): Promise<{ id: number; nama: string }[]> {
  const { data, error } = await supabase()
    .from('employees').select('id,nama')
    .eq('role', 'Teknisi').eq('outlet', outlet).eq('aktif', true).order('nama');
  if (error) throw new Error(error.message);
  return (data ?? []) as { id: number; nama: string }[];
}

export interface NewCustomer {
  nama: string; kat: 'User' | 'Toko'; wa: string; outlet: string;
  telp?: string; lahir?: string; email?: string; kota?: string; alamat?: string;
}

export async function createCustomer(c: NewCustomer): Promise<number> {
  const { data, error } = await supabase()
    .from('customers')
    .insert({
      nama: c.nama, kat: c.kat, telp: c.telp || c.wa, wa: c.wa,
      lahir: c.lahir || null, email: c.email || null, kota: c.kota || null, alamat: c.alamat || null,
      sumber: 'Walk-in', outlet: c.outlet,
    })
    .select('id').single();
  if (error) throw new Error(error.message);
  return (data as { id: number }).id;
}

/** Anti-double: cari pelanggan dgn WA/telp sama (cegah pelanggan ganda).
 *  Cek varian 62… DAN 0… agar cocok dgn data lama yg tersimpan tak-ternormalisasi (setara wa62 v0.18). */
export async function findCustomerByWa(wa: string): Promise<{ id: number; nama: string } | null> {
  if (!wa) return null;
  const alt = wa.startsWith('62') ? '0' + wa.slice(2) : wa; // wa = digit-only (wa62) → aman utk .or()
  const { data, error } = await supabase()
    .from('customers').select('id,nama')
    .or(`wa.eq.${wa},telp.eq.${wa},wa.eq.${alt},telp.eq.${alt}`).limit(1);
  if (error) throw new Error(error.message);
  return (data?.[0] as { id: number; nama: string } | undefined) ?? null;
}

export async function listOutlets(): Promise<{ kode: string; nama: string }[]> {
  const { data, error } = await supabase().from('outlets').select('kode,nama').order('kode');
  if (error) throw new Error(error.message);
  return (data ?? []) as { kode: string; nama: string }[];
}

export interface TicketInsert {
  no: string; token: string; customer_id: number; outlet: string; status: string;
  jenis: string; merk: string; model: string; sn: string; warna: string; unit: string;
  kelengkapan: string[]; kondisi: string; sandi: string; keluhan: string;
  est_biaya: number; est_selesai: string | null; lama: string; ket: string;
  dp: number; dp_via?: string | null; prioritas: string; catatan: string; penerima: string; teknisi: string;
  hold: boolean; kerja: string | null; tgl_iso: string;
  dugaan?: number | null; case_khusus?: boolean; case_khusus_ket?: string | null;
  /** Foto kondisi fisik saat diterima — daftar jalur di bucket privat 'unit' (0147). */
  foto_unit?: { path: string; fase: 'terima' | 'serah'; at: string; by: string }[];
}

/** Kontak pelanggan + token utk kirim WA (link lacak) pasca-intake. */
export interface TicketWaData { wa: string; token: string; nama: string; unit: string; merk: string; tipe: string; kerusakan: string }
export async function getTicketContact(no: string): Promise<TicketWaData> {
  const { data, error } = await supabase()
    .from('tickets').select('token,unit,merk,model,keluhan,customers(nama,wa)').eq('no', no).single();
  if (error) throw new Error(error.message);
  const r = data as { token: string | null; unit: string | null; merk: string | null; model: string | null; keluhan: string | null; customers: { nama?: string; wa?: string } | { nama?: string; wa?: string }[] | null };
  const c = Array.isArray(r.customers) ? r.customers[0] : r.customers;
  // Merk & tipe DIPISAH: pesan WA ditulis berbaris "Merk handphone : … / Tipe : …" (format Apo)
  // supaya terbaca sekilas di HP. `unit` (gabungan) tetap dibawa utk pemakaian lama.
  return {
    wa: (c?.wa ?? '').replace(/\D/g, ''), token: r.token ?? '', nama: c?.nama ?? 'pelanggan',
    unit: r.unit ?? '', merk: r.merk ?? '', tipe: r.model ?? '', kerusakan: r.keluhan ?? '',
  };
}

export async function addLog(no: string, status: string, catatan: string, by: string): Promise<void> {
  const { error } = await supabase().from('ticket_logs')
    .insert({ ticket_no: no, status, catatan, by_user: by });
  if (error) throw new Error(error.message);
}

/** Dilempar bila nomor servis sudah dipakai (kunci utama tabel) — pemanggil mencoba nomor lain. */
export class NomorBentrok extends Error {}

export async function createTicket(t: TicketInsert, by: string): Promise<void> {
  const { error } = await supabase().from('tickets').insert(t);
  // 23505 = kunci utama ganda. Dua kasir bisa menekan Simpan pada detik yang sama; peluang
  // nomor acak yang sama kecil, tapi bukan nol — dan kalau terjadi, staf tak boleh melihat
  // pesan Postgres. Database tetap yang menjamin nomor unik, ini hanya membuatnya ramah.
  if (error && (error.code === '23505' || /duplicate key|tickets_pkey/i.test(error.message))) throw new NomorBentrok(error.message);
  if (error) throw new Error(error.message);
  await addLog(t.no, 'Diterima', 'Unit diterima di counter', by);
}
